/**
 * Routine API contract — frontend (ES modules).
 *
 * MIRROR: keep in sync with functions/contracts/routineApi.js
 *
 * The Routine is the persistent home for the assembled product plan (blueprint
 * §6.4). Each active is a strength ladder; the rung climbs with progression.
 * Adherence is the user's self-report ("are you using this?"), persisted on the
 * routine doc. Efficacy verdicts are derived from the stored adherence value via
 * ADHERENCE_META (the full re-scan-gated climbing loop is deferred — see plan).
 *
 * @typedef {("yes"|"partial"|"no")} AdherenceValue
 *
 * @typedef {Object} GetRoutineResponse
 * @property {RoutineActive[]} am
 * @property {RoutineActive[]} pm
 * @property {string} total_price
 * @property {number} stage
 * @property {boolean} efficacy_on
 *
 * setAdherence request: { active_id: string, value: AdherenceValue }
 * setAdherence response: { ok: true }
 */

export const ADHERENCE_VALUES = Object.freeze(["yes", "partial", "no"]);

// Display + derived-efficacy metadata per adherence value (prototype ADH map).
export const ADHERENCE_META = Object.freeze({
  yes: {
    label: "Used consistently", dot: "#8FD2A0",
    verdict: "Working — a stronger rung is a reasonable next step.",
    color: "#C9A45C", delta: "+0.4",
  },
  partial: {
    label: "Used sometimes", dot: "#C9A77A",
    verdict: "Some progress — holding at this rung.",
    color: "#C9A77A", delta: "+0.1",
  },
  no: {
    label: "Not used", dot: "#56524A",
    verdict: "No confirmed use — unchanged next scan.",
    color: "#8A857C", delta: "—",
  },
});

// ── THE ADHERENCE BONUS ─────────────────────────────────────────────────────
//
// ADHERENCE_META publishes a delta per value on the DISPLAYED 1-10 scale
// ("+0.4" / "+0.1" / "—"). Scores are carried on the 1-100 scale, so the parsed
// delta is scaled by 10 and the total is capped — a long routine must not be
// able to stack its way to a meaningless number.
//
// This lives in the contract rather than in fuseScores because two surfaces now
// depend on the identical arithmetic: the backend APPLIES it to the score, and
// the glowup modal PROJECTS it ("if you stay consistent"). A second copy of the
// formula would let the promise drift from the payout.
export const MAX_ADHERENCE_BONUS = 10; // 1-100 scale; 1.0 on the displayed 1-10

/** @param {unknown} value @returns {number} delta on the 1-10 display scale */
export function adherenceDelta(value) {
  const meta = ADHERENCE_META[value];
  if (!meta) return 0;
  const n = parseFloat(meta.delta); // "—" parses to NaN → 0
  return Number.isFinite(n) ? n : 0;
}

/**
 * @param {Object<string, AdherenceValue>} adherence active_id -> reported value
 * @returns {number} 0..MAX_ADHERENCE_BONUS, on the 1-100 scale
 */
export function adherenceBonus(adherence) {
  if (!adherence || typeof adherence !== "object") return 0;
  const raw = Object.values(adherence).reduce((sum, v) => sum + adherenceDelta(v), 0);
  return Math.max(0, Math.min(MAX_ADHERENCE_BONUS, Math.round(raw * 10)));
}

export const ROUTINE_ERROR_CODES = Object.freeze({
  UNAUTHENTICATED: "UNAUTHENTICATED",
  NOT_FOUND: "NOT_FOUND",
  FORBIDDEN: "FORBIDDEN",
  INVALID_INPUT: "INVALID_INPUT",
  INTERNAL: "INTERNAL",
});

/** @param {unknown} v @returns {boolean} */
export function isAdherenceValue(v) {
  return ADHERENCE_VALUES.includes(v);
}
